import {
  type MouseEventHandler,
  type FocusEventHandler,
  useEffect,
  useState,
  type KeyboardEventHandler,
} from "react";
// This rule is being ignored because we want devs to useAddressAutocomplete instead of usePlacesAutocomplete
// The exception being this file where we are creating a new hook that uses usePlacesAutocomplete
// eslint-disable-next-line no-restricted-imports
import useOriginalPlacesAutocomplete, {
  type RequestOptions,
  type Suggestion as OriginalSuggestion,
} from "use-places-autocomplete";

interface Suggestion
  extends Omit<OriginalSuggestion, "place_id" | "structured_formatting" | "matched_substrings"> {
  id: string;
  active: boolean;
  placeId: OriginalSuggestion["place_id"];
  matchedSubstrings: OriginalSuggestion["matched_substrings"];
  formattedSuggestion: {
    mainText: OriginalSuggestion["structured_formatting"]["main_text"];
    secondaryText: OriginalSuggestion["structured_formatting"]["secondary_text"];
  };
}

type GetSuggestionItemProps = <SuggestionProps extends {}>(
  suggestion: Suggestion,
  options?: SuggestionProps
) => {
  key: string;
  id: string;
  role: string;
  onClick: MouseEventHandler;
  onMouseDown: MouseEventHandler;
};

interface Params {
  defaultValue: string;
  requestOptions?: RequestOptions;
  onChange?: (value: string) => void;
  onSelect?: (address: string, placeID: string, suggestion: Suggestion) => void;
  shouldFetchSuggestions?: boolean;
}

type Response = Readonly<{
  getSuggestionItemProps: GetSuggestionItemProps;
  loading: boolean;
  onChange: (ev: { target: { value: string } }) => void;
  onBlur: FocusEventHandler;
  onKeyDown: KeyboardEventHandler;
  suggestions: readonly Suggestion[];
  value: string;
}>;

type UseAddressAutocomplete = (params: Params) => Response;

/**
 * Used to handle address input autocomplete functionality
 * Use in conjunction with AddressPulldownView for best results
 *
 * @see `@shared/components/Fields/AddressInfoFields/AddressInfoFields.tsx` for a usage example
 */
const useAddressAutocomplete: UseAddressAutocomplete = ({
  defaultValue,
  onChange,
  onSelect,
  requestOptions,
  shouldFetchSuggestions,
}) => {
  const [activeIndex, setActiveIndex] = useState<number>(null);

  const {
    value,
    suggestions: { loading, status, data },
    setValue,
    clearSuggestions,
  } = useOriginalPlacesAutocomplete({
    callbackName: "Function.prototype",
    defaultValue,
    requestOptions,
    debounce: 200,
  });

  const suggestions: Suggestion[] = data.map((suggestion, index) => ({
    id: suggestion.place_id,
    active: index === activeIndex,
    description: suggestion.description,
    placeId: suggestion.place_id,
    formattedSuggestion: {
      mainText: suggestion.structured_formatting.main_text,
      secondaryText: suggestion.structured_formatting.secondary_text,
    },
    matchedSubstrings: suggestion.matched_substrings,
    terms: suggestion.terms,
    types: suggestion.types,
  }));

  useEffect(() => {
    if (status !== "OK") {
      handleClearSuggestions();
    }
  }, [status]);

  // In some situations, if this hook initializes before a external query finishes
  // value might not take the updated defaultValue
  useEffect(() => {
    setValue(defaultValue, false);
  }, [defaultValue]);

  const handleClearSuggestions = () => {
    clearSuggestions();
    setActiveIndex(null);
  };

  const handleChange = e => {
    setValue(e.target.value, shouldFetchSuggestions ?? true);
    onChange?.(e.target.value);
  };

  const handleEnterKey = () => {
    if (suggestions.length === 0 || activeIndex === null) {
      return;
    }

    handleSelect(suggestions[activeIndex]);
  };

  const handleDownKey = () => {
    if (suggestions.length === 0) {
      return;
    }

    if (activeIndex === null) {
      setActiveIndex(0);
    } else {
      setActiveIndex(currentActiveIndex => {
        if (currentActiveIndex + 1 === suggestions.length) {
          return null;
        }

        return currentActiveIndex + 1;
      });
    }
  };

  const handleUpKey = () => {
    if (suggestions.length === 0) {
      return;
    }

    if (activeIndex === null) {
      setActiveIndex(suggestions.length - 1);
    } else {
      setActiveIndex(currentActiveIndex => {
        if (currentActiveIndex === 0) {
          return null;
        }

        return currentActiveIndex - 1;
      });
    }
  };

  const handleKeyDown: KeyboardEventHandler = e => {
    if (e.key === "Enter" || e.key === "ArrowDown" || e.key === "ArrowUp" || e.key === "Escape") {
      e.preventDefault();
    }

    if (e.key === "Enter") {
      handleEnterKey();
    } else if (e.key === "ArrowDown") {
      handleDownKey();
    } else if (e.key === "ArrowUp") {
      handleUpKey();
    } else if (e.key === "Escape") {
      handleClearSuggestions();
    }
  };

  const handleBlur: FocusEventHandler = () => {
    clearSuggestions();
  };

  const handleMouseDown: MouseEventHandler = e => {
    // onBlur gets invoked before onClick
    // This prevents the suggestion from being selected properly
    // calling preventDefault on mousedown prevents onBlur from being called allowing onClick to be called
    e.preventDefault();
  };

  const handleSelect = (suggestion: Suggestion) => {
    const { description, placeId } = suggestion;

    // Don't retrieve new suggestions by passing false as third argument
    setValue(suggestion.formattedSuggestion.mainText, false);

    handleClearSuggestions();
    onSelect(description, placeId, { active: true, ...suggestion });
  };

  const getSuggestionItemProps: GetSuggestionItemProps = (suggestion, options) => {
    return {
      key: suggestion.placeId,
      id: suggestion.placeId,
      role: "option",
      onClick: (() => handleSelect(suggestion)) as MouseEventHandler,
      onMouseDown: handleMouseDown,
      ...options,
    };
  };

  return {
    onBlur: handleBlur,
    onChange: handleChange,
    onKeyDown: handleKeyDown,
    value,
    suggestions,
    getSuggestionItemProps,
    loading,
  };
};

export { useAddressAutocomplete };
export type { UseAddressAutocomplete, Suggestion, GetSuggestionItemProps, Params };
